import React from 'react';
import { Pencil, Trash2, ImageOff, ToggleLeft, ToggleRight } from 'lucide-react';
import { Personagem } from '../../../types/personagem';
import { PersonagemStats } from '../../../utils/personagemStats';
import { formatCurrency } from '../../../utils/currencyUtils';

interface PersonagemCardProps {
  personagem: Personagem;
  stats: PersonagemStats;
  loadingStats: boolean;
  onOpenDetail: (personagem: Personagem) => void;
  onOpenImage: (imageUrl: string) => void;
  onEdit: (personagem: Personagem) => void;
  onDelete: (personagem: Personagem) => void;
  onToggleAtivo: (personagem: Personagem) => void;
}

export const PersonagemCard: React.FC<PersonagemCardProps> = ({
  personagem,
  stats,
  loadingStats,
  onOpenDetail,
  onOpenImage,
  onEdit,
  onDelete,
  onToggleAtivo
}) => {
  const stopPropagation = (e: React.MouseEvent, action: () => void) => {
    e.stopPropagation();
    action();
  };

  return (
    <div
      onClick={() => onOpenDetail(personagem)}
      className="relative bg-white rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow duration-200 flex flex-col overflow-hidden cursor-pointer"
    >
      {/* Faixa gradiente */}
      <div className="h-16 bg-gradient-to-r from-teal-500 to-teal-600 relative">
        <span
          className={`absolute top-2 left-3 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ${
            personagem.ativo ? 'bg-white/90 text-teal-700' : 'bg-black/30 text-white'
          }`}
        >
          {personagem.ativo ? 'Ativo' : 'Inativo'}
        </span>
      </div>

      {/* Foto do personagem, sobreposta à faixa */}
      <button
        onClick={(e) => personagem.fotoUrl && stopPropagation(e, () => onOpenImage(personagem.fotoUrl!))}
        className="absolute right-3 top-2 w-24 h-24 rounded-2xl border-4 border-white shadow-lg bg-slate-100 overflow-hidden flex items-center justify-center"
        title={personagem.fotoUrl ? 'Ampliar foto' : undefined}
      >
        {personagem.fotoUrl ? (
          <img src={personagem.fotoUrl} alt={personagem.nome} className="w-full h-full object-cover" />
        ) : (
          <ImageOff className="w-8 h-8 text-slate-300" />
        )}
      </button>

      <div className="p-4 pt-5 flex-1 flex flex-col">
        <div className="pr-24">
          <span className="block text-xs font-semibold text-slate-400 uppercase tracking-wide">Preço base</span>
          <span className="block text-2xl font-bold text-slate-900">
            {personagem.precoBase ? formatCurrency(personagem.precoBase) : 'A definir'}
          </span>

          <span className="block mt-2 text-xs font-semibold text-emerald-500 uppercase tracking-wide">
            Valor arrecadado
          </span>
          <span className="block text-lg font-bold text-emerald-600">
            {loadingStats
              ? 'Calculando...'
              : stats.totalEventos > 0
                ? formatCurrency(stats.receitaTotal)
                : 'Ainda não usado'}
          </span>
        </div>

        <h3 className="mt-3 text-base font-semibold text-slate-800 truncate pr-20">{personagem.nome}</h3>
        {personagem.categoria && <p className="text-xs text-slate-500">{personagem.categoria}</p>}
        {personagem.descricao && (
          <p className="mt-1 text-sm text-slate-500 line-clamp-2">{personagem.descricao}</p>
        )}

        <div className="mt-auto pt-4 border-t border-slate-100 flex items-center justify-between mt-4">
          <button
            onClick={(e) => stopPropagation(e, () => onToggleAtivo(personagem))}
            className="text-slate-400 hover:text-teal-600 transition-colors"
            title={personagem.ativo ? 'Desativar personagem' : 'Ativar personagem'}
          >
            {personagem.ativo ? (
              <ToggleRight className="w-6 h-6 text-teal-600" />
            ) : (
              <ToggleLeft className="w-6 h-6" />
            )}
          </button>

          <div className="flex items-center gap-2">
            <button
              onClick={(e) => stopPropagation(e, () => onEdit(personagem))}
              className="p-2 text-slate-500 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-colors"
              title="Editar"
            >
              <Pencil className="w-4 h-4" />
            </button>
            <button
              onClick={(e) => stopPropagation(e, () => onDelete(personagem))}
              className="p-2 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              title="Excluir"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
